import { getLocalDateString } from './formatters';

const GUEST_STREAK_KEY = 'puzzlists_guest_streak';

/**
 * Get guest streak from localStorage
 */
export const getGuestStreak = () => {
  try {
    const stored = localStorage.getItem(GUEST_STREAK_KEY);
    if (!stored) {
      return { current: 0, longest: 0, lastCompletionDate: null };
    }
    return JSON.parse(stored);
  } catch {
    return { current: 0, longest: 0, lastCompletionDate: null };
  }
};

/**
 * Update guest streak in localStorage when completing a puzzle
 * Returns the updated streak object
 */
export const updateGuestStreak = () => {
  const stored = getGuestStreak();

  const today = getLocalDateString(); // Use local date, not UTC
  const lastDate = stored.lastCompletionDate;

  if (!lastDate) {
    // First completion ever
    stored.current = 1;
  } else if (lastDate === today) {
    // Already completed today - don't increment
    return stored;
  } else {
    const daysDiff = Math.floor(
      (new Date(today) - new Date(lastDate)) / (1000 * 60 * 60 * 24),
    );
    if (daysDiff === 1) {
      // Consecutive day - extend streak
      stored.current += 1;
    } else {
      // Gap > 1 day - reset streak
      stored.current = 1;
    }
  }

  stored.longest = Math.max(stored.longest, stored.current);
  stored.lastCompletionDate = today;

  localStorage.setItem(GUEST_STREAK_KEY, JSON.stringify(stored));
  return stored;
};

/**
 * Get streak for display - works for both logged-in users and guests
 * @param {Object|null} user - User object from AuthContext (null for guests)
 * @returns {{ current: number, longest: number }}
 */
export const getStreak = (user) => {
  if (user && user.streak) {
    // Server already validates streak for logged-in users
    return {
      current: user.streak.current || 0,
      longest: user.streak.longest || 0,
    };
  }

  // Guest user - validate streak locally
  const guestStreak = getGuestStreak();
  let effectiveCurrent = guestStreak.current || 0;

  if (guestStreak.lastCompletionDate && effectiveCurrent > 0) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const lastDate = new Date(guestStreak.lastCompletionDate);
    lastDate.setHours(0, 0, 0, 0);

    const daysDiff = Math.floor((today - lastDate) / (1000 * 60 * 60 * 24));

    // daysDiff > 1 means streak is broken
    if (daysDiff > 1) {
      effectiveCurrent = 0;
    }
  }

  return {
    current: effectiveCurrent,
    longest: guestStreak.longest || 0,
  };
};
